<template>
  <div :class="['card', { ['card--without-document']: isEmptyCard }]">
    <div class="card__section">
      <div
        v-if="isEmptyCard"
        class="card__section__empty"
      >
        <BasicText
          color="white"
          wrapp
        >
          {{ documentConfig[type].emptyLabel }}
        </BasicText>
      </div>
      <template v-else>
        <div class="card__header">
          <div :class="`card__header__icon card__header__icon--${type}`">
            <BasicIcon :name="documentConfig[type].iconName" />
          </div>
          <BasicText
            size="h4"
            weight="bold"
          >
            {{ documentConfig[type].typeLabel }}
          </BasicText>
          <div></div>
        </div>

        <div class="card__body">
          <div
            class="card__body__item"
            v-for="(item, idx) in documentCardsData"
            :key="idx"
          >
            <BasicButton
              v-if="getStatus(item) === 'error'"
              label="Réessayer"
              @click="$emit('retry-fetch', item.variant)"
            />
            <BasicLoader v-else-if="getStatus(item) === 'loading'" />
            <template v-else-if="getStatus(item) === 'slowLoading'">
              <BasicText
                size="h3"
                weight="bold"
                :class="`card__body__item__text--${type}`"
              >
                ...
              </BasicText>
              <BasicLink
                label="Chargement"
                type="dark"
                size="small"
                disabled
              />
            </template>
            <div
              v-else
              class="card__body__item--data"
            >
              <BasicText
                size="h2"
                weight="bold"
                :class="['card__body__item__text', `card__body__item__text--${type}`]"
              >
                {{ item.count }}
              </BasicText>
              <BasicLink
                :label="item.label as string"
                type="dark"
                @click="$emit('redirection-document', item.variant)"
                nb-max-lines="3"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
    <WrapPermissions
      :permissions
      v-slot="{ isNotAllowed }"
    >
      <BasicButton
        :label="documentConfig[type].buttonLabel"
        type="secondary"
        variant="outlined"
        @click="$emit('ajout-document')"
        :disabled="isNotAllowed || !isActionAvailable(type)"
      />
    </WrapPermissions>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { documentConfig, VariantType, type DocumentCardProps, type DocumentCardEmit } from './DocumentCard.types'
  import WrapPermissions from '@/features/interface/shared/wrap-permissions/WrapPermissions.vue'
  import { useAjoutDocumentAction } from './useAjoutDocumentAction'

  const props = defineProps<DocumentCardProps>()

  defineEmits<DocumentCardEmit>()

  const { isActionAvailable } = useAjoutDocumentAction()

  const documentCardsData = computed(() => [
    {
      variant: 'primary' as VariantType,
      count: props.indicateurs.primary.count,
      label: props.indicateurs.primary.titre,
      loading: props.indicateurs.primary.loading,
      slowLoading: props.indicateurs.primary.slowLoading,
      error: props.indicateurs.primary.error,
    },
    {
      variant: 'secondary' as VariantType,
      count: props.indicateurs.secondary.count,
      label: props.indicateurs.secondary.titre,
      loading: props.indicateurs.secondary.loading,
      slowLoading: props.indicateurs.secondary.slowLoading,
      error: props.indicateurs.secondary.error,
    },
  ])

  const isEmptyCard = computed(() => {
    const isLoading = Object.values(props.indicateurs).some((i) => i.loading || i.slowLoading)
    if (isLoading) {
      return false
    }
    const totalPrimary = props.indicateurs.primary.count ?? 0
    const totalSecondary = props.indicateurs.secondary.count ?? 0
    return totalPrimary <= 0 && totalSecondary <= 0
  })

  function getStatus(item: {
    error?: boolean
    slowLoading?: boolean
    loading?: boolean
  }): 'error' | 'slowLoading' | 'loading' | 'normal' {
    if (item.error) return 'error'
    if (item.slowLoading) return 'slowLoading'
    if (item.loading) return 'loading'
    return 'normal'
  }
</script>

<style scoped lang="less">
  @import './DocumentCard.less';
</style>
